import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AppConfigService } from '../../services/app-config.service';
import { Observable, of, catchError, take, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { isLandingRedirectAllowed, profileRouteNavigator } from '../helpers/profileRouteNavigator.helpers';
import { UserRole } from '../constants/keys.constant';
import { ProfileStepsCompleted } from '../constants/signUpStepper.const';

export const profileResolver: ResolveFn<any> = (route, state): Observable<any> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const appConfigService = inject(AppConfigService);

  // Bypass resolver for direct token-based login/signup
  if (state.url.includes('/auth/login?token')) {
    return of(null);
  }

  // The Tutor/Counsellor/Parent/Student activation link is opened by a guest using their
  // own activation token, not by whoever is currently (or previously) logged in on this
  // browser/device. A stale session's profile must never redirect this page away —
  // otherwise a device with an existing logged-in user (or a leftover token from a
  // previous session) bounces the invitee to /dashboard before they can set a password.
  if (state.url.startsWith('/auth/set-password')) {
    return of(null);
  }

  if (!authService.currentProfileDetails && localStorage.getItem(environment.storageKey)) {
    return authService.getProfile().pipe(
        take(1),
        tap((profile) => {
          if (profile) {
            authService.profileDetailsSubject.next(profile);
            appConfigService.loadConfig().pipe(take(1)).subscribe();
            if ((profile.role === UserRole.TUTOR || profile.role === UserRole.COUNSELOR) && (profile.profileStepsCompleted === ProfileStepsCompleted.TutorCounsellorAvailabilityDone || profile.profileStepsCompleted === ProfileStepsCompleted.TutorCounsellorProfileDone)) {
              // Only send them to their dashboard when they have no page of their own
              // to land on — refreshing a shared page (chat, notifications, calendar…)
              // must not bounce them away from it.
              if (isLandingRedirectAllowed(state.url)) {
                router.navigate(['/tutor/dashboard']);
              }
            } else {
              if (state.url === '/auth/login?isIFrame=true' || state.url === '/auth/signup?isIFrame=true') {
                return;
              }
              profileRouteNavigator(profile, router, state.url);
            }
          }
        }),
        catchError((error) => {
          console.error('Error fetching profile:', error);
          return of(null);
        })
      );
  }

  // Return cached profile as an observable
  return authService.profileDetailsSubject.asObservable();
};
